
import $util from '../../prototype/util';

// margin around document edge not to occupy, in pixels
const edgeMargin = 4;

// compensate for inaccurate implementations of getElementFromPoint()
const occlusionFudge = 1;

// frequency in ms to poll position; this detects changes from anchor sibling movement
const pollFreq = 500;

const INT_MAX = 2e9;
const invert = {
    'top': 'bottom',
    'bottom': 'top',
    'left': 'right',
    'right': 'left'
};

// overflow values that support user scrolling
const scrolling = {
    auto: true,
    overlay: true,
    scroll: true
};


/**
 * Overlay with fixed position relative to anchor.
 */
export default {
    props: {
        /**
         * Element to anchor to
         */
        anchor: [Element,Object],
        /**
         * Position relative to anchor [top, right, bottom, left, start, end]; default is bottom;
         * may be inverted if available space is inadequate or there is an existing anchor.
         */
        position: {
            type: String,
            default: 'bottom'
        },
        /**
         * Alignment relative to anchor [start, center, end, grow, left, right]; default is start.
         * This is advisory and alignment can change to accommodate screen space.
         */
        align: {
            type: String,
            default: 'center'
        },
        /**
         * If `true`, close on click or touch anywhere outside element or anchor.
         */
        persistent: Boolean
    },

    data() {
        return {
            visible: false,     // If true, this overlay is anchored and visible
            occluded: false,    // If true, the anchor is occluded
            nested: false,      // If true, this overlay is the child of another overlay
            actual: null,       // Actual position; may be different from requested position
            origin: {},         // CSS styles to be applied to arrow element
            ios_dy: 0
        };
    },

    computed: {
        offset() {
            return 0;
        },
        anchorEl() {
            return this.anchor?.$el || this.anchor;
        },
        inlineLeft() {
            return this.$rtl ? 'end' : 'start';
        },
        inlineRight() {
            return this.$rtl ? 'start' : 'end';
        }
    },

    watch: {
        anchor(to, from) {
            this.anAnchor(to?.$el || to, from?.$el || from);
        },

        position() {
            this.anReset();
            this.anSync();
        },

        align() {
            this.anReset();
            this.anSync();
        }
    },

    created() {
        if ($build.env != 'production') {
            const position = this.position;
            if (position != 'top' && position != 'bottom' && position != 'left' && position != 'right' && position != 'start' && position != 'end') {
                throw new RangeError('Invalid position');
            }
            const align = this.align;
            if (align != 'start' && align != 'center' && align != 'end' && align != 'grow' && align != 'left' && align != 'right') {
                throw new RangeError('Invalid align');
            }
        }
    },

    mounted() {
        if (window.MutationObserver) {
            this.mo = new MutationObserver(this.anMutate);
        }
        this.anAnchor(this.anchorEl);
    },

    beforeDestroy() {
        this.anAnchor(null);
    },

    methods: {
        open() {
            if (!this.visible && this.anchorEl) {
                this.visible = true;
                this.nested = !!this.$parent.$overlay();
                this.ios_dy = 0;
                this.onOpen();
                this.anSync();
                this.anInt = setInterval(this.anWatch, pollFreq);
                addEventListener('resize', this.anSync);
                if (!this.persistent) {
                    const doc = document.documentElement;
                    doc.addEventListener('mousedown', this._dismiss, true);
                    doc.addEventListener('touchstart', this._dismiss, true);
                }
            }
        },

        close() {
            if (this.visible) {
                this.visible = false;
                this.onClose();
                this.$nextTick(this.anReset);
                clearInterval(this.anInt);
                this.anInt = 0;
                removeEventListener('resize', this.anSync);
                if (!this.persistent) {
                    const doc = document.documentElement;
                    doc.removeEventListener('mousedown', this._dismiss, true);
                    doc.removeEventListener('touchstart', this._dismiss, true);
                }
            }
        },

        toggle() {
            if (this.visible) {
                this.close();
            } else {
                this.open();
            }
        },

        /**
         * Monitor anchor position; sync if necessary.
         * @private
         */
        anWatch() {
            const ar = this.anchorEl.getBoundingClientRect();
            if (this.lastx !== ar.x ||
                this.lasty !== ar.y ||
                this.lastw !== ar.width ||
                this.lasth !== ar.height) {
                this.anSync();
            }
        },

        /**
         * Sync position with anchor.
         * @private
         */
        anSync() {
            if ((this.visible || this.persistent) && this.anchorEl) {
                const doc = document.documentElement;
                const anchor = this.anchorEl;
                const dw = doc.clientWidth;
                const dh = doc.clientHeight;
                const style = this.$el.style;
                const ar = anchor.getBoundingClientRect();
                const or = $util.boundingRect(this.$refs.content || this.$el, this.$el);
                const off = this.offset;
                const hc = (ar.left + ar.right) / 2;
                const vc = (ar.top + ar.bottom) / 2;
                let actual, occ1x, occ1y, occ2x, occ2y, occ3x, occ3y;

                // Detect need to invert positioning due to available space
                switch (this.position) {
                case 'top':
                    actual = (or.height + off > ar.top && dh - ar.bottom > ar.top) ? 'bottom' : 'top';
                    break;
                case 'bottom':
                    actual = (or.height + off > dh - ar.bottom && ar.top > dh - ar.bottom) ? 'top' : 'bottom';
                    break;
                case 'left':
                case this.inlineLeft:
                    actual = (or.width + off > ar.left && dw - ar.right > ar.left) ? 'right' : 'left';
                    break;
                case 'right':
                case this.inlineRight:
                    actual = (or.width + off > dw - ar.right && ar.left > dw - ar.right) ? 'left' : 'right';
                    break;
                }

                let retry;
                do {
                    if (this.actual !== actual) {
                        this.actual = actual;
                        this.anReset();
                    }

                    // 'position' positioning
                    switch (actual) {
                    case 'top':
                        style.bottom = dh - ar.top + off - this.ios_dy + 'px';
                        style.maxHeight = ar.top - off + 'px';
                        occ1x = hc;
                        occ2x = hc - ar.width / 3;
                        occ3x = hc + ar.width / 3;
                        occ1y = occ2y = occ3y = ar.top + occlusionFudge;
                        break;
                    case 'bottom':
                        style.top = ar.bottom + off + this.ios_dy + 'px';
                        style.maxHeight = dh - ar.bottom - off + 'px';
                        occ1x = hc;
                        occ2x = hc - ar.width / 3;
                        occ3x = hc + ar.width / 3;
                        occ1y = occ2y = occ3y = ar.bottom - 1 - occlusionFudge;
                        break;
                    case 'left':
                        style.right = dw - ar.left + off + 'px';
                        style.maxWidth = ar.left - off + 'px';
                        occ1x = occ2x = occ3x = ar.left + occlusionFudge;
                        occ1y = vc;
                        occ2y = vc - ar.height / 3;
                        occ3y = vc + ar.height / 3;
                        break;
                    case 'right':
                        style.left = ar.right + off + 'px';
                        style.maxWidth = dw - ar.right - off + 'px';
                        occ1x = occ2x = occ3x = ar.right - 1 - occlusionFudge;
                        occ1y = vc;
                        occ2y = vc - ar.height / 3;
                        occ3y = vc + ar.height / 3;
                        break;
                    }

                    // Occlusion detection
                    this.occluded =
                        !$util.isChildOrSelf(document.elementFromPoint(occ1x, occ1y), anchor) &&
                        !$util.isChildOrSelf(document.elementFromPoint(occ2x, occ2y), anchor) &&
                        !$util.isChildOrSelf(document.elementFromPoint(occ3x, occ3y), anchor);
                    if (this.occluded) {
                        if (retry) {
                            this.close();
                            return;
                        } else {
                            // Desired position is occluded; try inverting
                            actual = invert[actual];
                            retry = true;
                        }
                    }
                }
                while (retry);

                // 'align' positioning
                if (actual == 'top' || actual == 'bottom') {
                    switch (this.align) {
                    case 'center':
                        if (hc > dw / 2) {
                            if (this.anHalf !== 1) {
                                this.anHalf = 1;
                                style.left = '';
                                style.right = edgeMargin + 'px';
                            }
                            style.minWidth = (dw - hc) * 2 - edgeMargin + 'px';
                            this.origin.left = 'calc(100% - ' + (dw - hc - edgeMargin) + 'px)';
                        } else {
                            if (this.anHalf !== 0) {
                                this.anHalf = 0;
                                style.left = edgeMargin + 'px';
                                style.right = '';
                            }
                            style.minWidth = hc * 2 - edgeMargin + 'px';
                            this.origin.left = hc - edgeMargin + 'px';
                        }
                        break;
                    case 'left':
                    case this.inlineLeft:
                        style.minWidth = 'calc(100% - ' + (ar.left + edgeMargin - Math.max(0, off * 3 - ar.width / 2)) + 'px)';
                        this.origin.left = 'calc(100% - ' + (dw - ar.left - edgeMargin - Math.min(off * 3, ar.width / 2)) + 'px)';
                        break;
                    case 'right':
                    case this.inlineRight:
                        style.minWidth = ar.right - edgeMargin + Math.max(0, off * 3 - ar.width / 2) + 'px';
                        this.origin.left = ar.right - edgeMargin - Math.min(off * 3, ar.width / 2) + 'px';
                        break;
                    case 'grow':
                        style.left = ar.left + 'px';
                        style.minWidth = ar.width + 'px';
                        break;
                    }
                } else {
                    switch (this.align) {
                    case 'start':
                        style.minHeight = 'calc(100% - ' + (ar.top + edgeMargin - Math.max(0, off * 3 - ar.height / 2)) + 'px)';
                        this.origin.top = 'calc(100% - ' + (dh - ar.top - edgeMargin - Math.min(off * 3, ar.height / 2)) + 'px)';
                        break;
                    case 'center':
                        if (vc > dh / 2) {
                            if (this.anHalf !== 1) {
                                this.anHalf = 1;
                                style.top = '';
                                style.bottom = edgeMargin + 'px';
                            }
                            style.minHeight = (dh - vc) * 2 - edgeMargin + 'px';
                            this.origin.top = 'calc(100% - ' + (dh - vc - edgeMargin) + 'px)';
                        } else {
                            if (this.anHalf !== 0) {
                                this.anHalf = 0;
                                style.top = edgeMargin + 'px';
                                style.bottom = '';
                            }
                            style.minHeight = vc * 2 - edgeMargin + 'px';
                            this.origin.top = vc - edgeMargin + 'px';
                        }
                        break;
                    case 'end':
                        style.minHeight = ar.bottom - edgeMargin + Math.max(0, off * 3 - ar.height / 2) + 'px';
                        this.origin.top = ar.bottom - edgeMargin - Math.min(off * 3, ar.height / 2) + 'px';
                        break;
                    case 'grow':
                        style.top = ar.top + this.ios_dy + 'px';
                        style.minHeight = ar.height + 'px';
                        break;
                    }
                }

                // Save values for anWatch
                this.lastx = ar.x;
                this.lasty = ar.y;
                this.lastw = ar.width;
                this.lasth = ar.height;

                // iOS bug workaround: fixed position elements not showing up at requested position with software keyboard displayed
                if (this.$device.ios && !this.ios_dy) {
                    const st = parseFloat(style.top);
                    const bt = $util.boundingRect(this.$el, this.$el).top;
                    if (st > bt) {
                        this.ios_dy = st - bt;
                        style.top = st + st - bt + 'px';
                    }
                }

                if (!this.visible) {
                    this.open();
                }
            }
        },

        /**
         * Sync position after a mutation event.
         * @private
         */
        anSyncTo() {
            this.anTo = 0;
            this.anSync();
        },

        /**
         * Check if anchor or anchor ancestor has been hidden after attribute change.
         * @private
         */
        anMutate(mutations) {
            if (this.visible) {
                if (this.anchorEl.offsetParent == null) {
                    this.close();
                } else {
                    for (let i = 0; i < mutations.length; i++) {
                        const el = mutations[i].target;
                        const style = getComputedStyle(el);
                        if (style.display == 'none' || style.visibility == 'hidden') {
                            this.close();
                            break;
                        }
                    }
                }
                if (!this.anTo) {
                    this.anTo = setTimeout(this.anSyncTo, 72);
                }
            }
        },

        /**
         * Check if anchor or anchor ancestor has been hidden after transition.
         * @private
         */
        anTransition(e) {
            if (this.visible && (e.propertyName == 'display' || e.propertyName == 'visibility')) {
                const style = getComputedStyle(e.target);
                if (style.display == 'none' || style.visibility == 'hidden') {
                    this.close();
                } else if (!this.anTo) {
                    this.anTo = setTimeout(this.anSyncTo, 72);
                }
            }
        },

        /**
         * Called on create, destroy, or change in anchor.
         * @private
         */
        anAnchor(to, from) {
            const mo = this.mo;
            const obsnew = [];
            const obsold = this.obs || [];

            if (this.anTo) {
                clearTimeout(this.anTo);
                this.anTo = 0;
            }
            if (from && mo) {
                mo.disconnect();
            }
            if (to) {
                let el = to.parentElement;
                while (el && el !== document.documentElement) {
                    if (mo) {
                        mo.observe(el, { attributes: true });
                    }
                    obsnew.push(el);
                    el = el.parentElement;
                }
            }

            let newi = 0;
            while (newi < obsnew.length && obsnew[newi] === obsold[newi]) {
                newi++;
            }
            let oldi = newi;
            let scroll = true;

            // unobserve old parents
            while (oldi < obsold.length) {
                if (obsnew[oldi] !== document.body) {
                    obsold[oldi].removeEventListener('scroll', this.anSync);
                    obsold[oldi].removeEventListener('transitionend', this.anTransition);
                }
                oldi++;
            }

            // observe new parents
            while (newi < obsnew.length) {
                if (obsnew[newi] !== document.body) {
                    const style = getComputedStyle(obsnew[newi]);
                    if (style.transitionDuration !== '0s') {
                        obsnew[newi].addEventListener('transitionend', this.anTransition);
                    }
                    if (scroll) {
                        if (
                            scrolling[style.getPropertyValue('overflow-x')] ||
                            scrolling[style.getPropertyValue('overflow-y')]) {
                            obsnew[newi].addEventListener('scroll', this.anSync);
                        }
                        if (style.getPropertyValue('position') === 'fixed') {
                            scroll = false;
                        }
                    }
                }
                newi++;
            }

            // FIXME: use position:absolute instead of fixed so we don't need this
            if (!from) {
                addEventListener('scroll', this.anSync);
            }
            if (!to) {
                removeEventListener('scroll', this.anSync);
            }

            this.obs = obsnew;
            if (to) {
                this.$nextTick(this.anSync);
            } else {
                this.close();
            }
        },

        /**
         * Clear all manual CSS props and prepare for current position/align.
         * @private
         */
        anReset() {
            const style = this.$el.style;
            const actual = this.actual;
            const align = this.align;
            const off = this.offset;
            style.top =
            style.bottom =
            style.left =
            style.right =
            style.minWidth =
            style.minHeight =
            style.maxWidth =
            style.maxHeight = '';
            if (actual == 'top' || actual == 'bottom') {
                switch (align) {
                case 'center':
                    style.maxWidth = 'calc(100% - ' + (edgeMargin * 2) + 'px)';
                    this.anHalf = -1;
                    break;
                case 'left':
                case this.inlineLeft:
                    style.right = edgeMargin + 'px';
                    break;
                case 'right':
                case this.inlineRight:
                    style.left = edgeMargin + 'px';
                    break;
                }
                this.origin = {
                    top: actual == 'top' ? 'calc(100% + ' + off * 2 + 'px)' : 0,
                    left: null,
                    margin: -off * 2 + 'px',
                    width: off * 4 + 'px',
                    height: off * 2 + 'px'
                };
            } else {
                switch (align) {
                case 'start':
                    style.bottom = edgeMargin + 'px';
                    break;
                case 'center':
                    style.maxHeight = 'calc(100% - ' + (edgeMargin * 2) + 'px)';
                    this.anHalf = -1;
                    break;
                case 'end':
                    style.top = edgeMargin + 'px';
                    break;
                }
                this.origin = {
                    top: null,
                    left: actual == 'left' ? 'calc(100% + ' + off * 2 + 'px)' : 0,
                    margin: -off * 2 + 'px',
                    width: off * 2 + 'px',
                    height: off * 4 + 'px'
                };
            }
        },

        /**
         * Make overlay invisible due to touch or click, unless targeting overlay or anchor.
         * @private
         */
        _dismiss(e) {
            if (!e.button &&
                !$util.isChildOrSelf(e.target, this.$el) &&
                !$util.isChildOrSelf(e.target, this.anchorEl)) {
                this.close();
            }
        },

        onOpen() {
            // noop
        },

        onClose() {
            // noop
        }
    }
}
